import React, { useContext } from 'react';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostNetworksContext from '@totem/components/ubiquiti/hosts/details/networks/ubiquitiHostNetworksContext';
import {
  UbiquitiApiDataItem,
  UbiquitiNetwork,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const UbiquitiHostNetworksTable = () => {
  const { loading, error, networks } = useContext(UbiquitiHostNetworksContext);

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiNetwork>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'VLAN ID',
      dataIndex: ['data', 'vlanId'],
      key: 'vlanId',
      sorter: (compA, compB) => compA.data.vlanId - compB.data.vlanId,
    },
    {
      title: 'Management',
      dataIndex: ['data', 'management'],
      key: 'management',
      render: (_, record: UbiquitiApiDataItem<UbiquitiNetwork>) => (
        <Tag>{record.data.management}</Tag>
      ),
    },
    {
      title: 'Enabled',
      dataIndex: ['data', 'enabled'],
      key: 'enabled',
      render: (_, record: UbiquitiApiDataItem<UbiquitiNetwork>) =>
        record.data.enabled ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'Default',
      dataIndex: ['data', 'default'],
      key: 'default',
      render: (_, record: UbiquitiApiDataItem<UbiquitiNetwork>) =>
        record.data.default ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'Origin',
      dataIndex: ['data', 'metadata', 'origin'],
      key: 'origin',
      render: (_, record: UbiquitiApiDataItem<UbiquitiNetwork>) => (
        <Tag>{record.data.metadata.origin}</Tag>
      ),
    },
  ];

  const exportHeaders = [
    'Name',
    'VLAN ID',
    'Management',
    'Enabled',
    'Default',
    'Origin',
  ];
  const exportRows = networks.map((network) => [
    network.data.name,
    network.data.vlanId,
    network.data.management,
    network.data.enabled ? 'Yes' : 'No',
    network.data.default ? 'Yes' : 'No',
    network.data.metadata.origin,
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Networks unavailable"
        description="No network data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiNetwork>>
      showSorterTooltip
      columns={columns}
      dataSource={networks}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="networks"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostNetworksTable;
